/* eslint-disable max-len */
import React, { useState } from 'react';
import { FormattedMessage, defineMessages, useIntl } from 'react-intl';
import { Field, useFormikContext, Formik, Form } from 'formik';
import { useHistory } from 'react-router-dom';
import {
  DropdownWithValidationAndTooltip,
  InputTextWithValidationMessageAndToolip,
} from '../../../components/prime-formik';
import { ClientAgreementCheckbox } from '../../../components/AgreementCheckboxes';
import {
  buildEssentialAccountOrganizationDetailsStepFormInitialValues,
  buildEssentialAccountOrganizationDetailsStepFormValidationSchema,
} from '../helpers/account-invitation-helper';
import EssentialAccountOrganizationDetailStepButtons from './EssentialAccountOrganizationDetailStepButtons';
import TaxIdTypeInput from './TaxIdTypeInput';
import TaxIdInput from './TaxIdInput';
import OrganisationSizeInput from './OrganisationSizeInput';

const messages = defineMessages({
  addressTooltip: { id: 'attrs.address.tooltip', defaultMessage: 'The address of the legal entity' },
  regNoTooltip: {
    id: 'attrs.regNo.tooltip',
    defaultMessage:
      'The official registration number of the legal entity. If there is no registration number please enter "0".',
  },
  displayNameTooltip: {
    id: 'attrs.displayName.tooltip',
    defaultMessage: 'The name displayed in reports, invitations to candidates etc.',
  },
  dpoEmailTooltip: {
    id: 'attrs.dpoEmail.tooltip',
    defaultMessage: 'The e-mail address of the organisation’s Data Protection Officer',
  },
  email: { id: 'lbl.email', defaultMessage: 'E-mail address' },
  legalName: { id: 'attrs.legalName', defaultMessage: 'Legal Name' },
  legalNameTooltip: { id: 'attrs.legalName.tooltip', defaultMessage: 'The full name of the legal entity' },
  addressLine1: { id: 'attrs.addresLine1', defaultMessage: 'Address Line 1' },
  addressLine2: { id: 'accept-client-account.org-step.addressLine2', defaultMessage: 'Address Line 2 (optional)' },
  postalCode: { id: 'attrs.postalCode', defaultMessage: 'Postal Code' },
  city: { id: 'attrs.city', defaultMessage: 'City' },
  state: { id: 'attrs.state', defaultMessage: 'State (optional)' },
  country: { id: 'attrs.country', defaultMessage: 'Country' },
  registrationNumber: { id: 'attrs.regNo', defaultMessage: 'Registration Number' },
  vatCountry: { id: 'attrs.vatCountry', defaultMessage: 'VAT/GST country' },
  displayName: { id: 'attrs.displayName', defaultMessage: 'Display Name' },
  creditNotificationThreshold: {
    id: 'attrs.creditNotificationThreshold',
    defaultMessage: 'Credit Notification Threshold',
  },
  creditNotificationThresholdTooltip: {
    id: 'attrs.creditNotificationThreshold.tooltip',
    defaultMessage: 'If the credit balance goes below this value, interested parties are notified.',
  },
  dpo: { id: 'attrs.dpo', defaultMessage: 'Data Protection Officer (optional)' },
  vatIdNotValid: {
    id: 'attrs.vatIdNotValid',
    defaultMessage: 'VAT ID is not valid',
  },
});

const EssentialAccountOrganizationDetailsStep = ({
  countries,
  taxIdOptions,
  availableTaxIds,
  invitation,
  token,
  userExists,
}) => {
  const history = useHistory();
  const { formatMessage } = useIntl();
  const { setFieldValue, values } = useFormikContext();

  const [selectedTaxId, setSelectedTaxId] = useState(null);

  const {
    invitedBy: { email, fistName = '', lastName = '' },
  } = invitation;

  const initialValues = buildEssentialAccountOrganizationDetailsStepFormInitialValues(values);
  const validationSchema = buildEssentialAccountOrganizationDetailsStepFormValidationSchema(selectedTaxId);

  const handleSubmit = (values) => {
    setFieldValue('company', { ...values.company });
    setFieldValue('client_terms_of_use_agreement', values.client_terms_of_use_agreement);

    let redirectUrl = `/account-invitation/other-users?token=${token}`;

    redirectUrl = userExists ? `${redirectUrl}&userExists=true` : redirectUrl;

    history.push(redirectUrl);
  };

  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={(values) => {
        handleSubmit(values);
      }}
    >
      <Form>
        <div className="p-col-12">
          <FormattedMessage
            id="accept-account-invitation.form.heading-title"
            defaultMessage="Hello, {invitee},"
            tagName="h2"
            values={{
              invitee: invitation.fullName,
            }}
          />
          <FormattedMessage
            id="accept-account-invitation.message"
            defaultMessage="You have been invited by {inviterFullName} ({inviterEmail}) from {partnerAccountName} to create a new account. Please follow the instructions bellow."
            tagName="p"
            values={{
              inviterFullName: <strong>{`${fistName} ${lastName}`}</strong>,
              inviterEmail: email,
              partnerAccountName: <strong>{invitation.partner.displayName}</strong>,
            }}
          />
        </div>
        <div className="p-col-12">
          <FormattedMessage
            id="accept-account-invitation.org-details.heading"
            defaultMessage="Organisational details"
            tagName="h2"
          />
          <FormattedMessage
            id="accept-account-invitation.org-details.instructions"
            defaultMessage="Please enter your organisation's legal name, invoice address and other account specific information."
            tagName="p"
          />
        </div>
        <div className="p-col-12">
          <Field
            name="company.legalName"
            labelText={formatMessage(messages.legalName)}
            component={InputTextWithValidationMessageAndToolip}
            tooltipText={formatMessage(messages.legalNameTooltip)}
          />
        </div>
        <div className="p-col-12">
          <Field
            name="company.addressLine1"
            labelText={formatMessage(messages.addressLine1)}
            component={InputTextWithValidationMessageAndToolip}
            tooltipText={formatMessage(messages.addressTooltip)}
          />
        </div>
        <div className="p-col-12">
          <Field
            name="company.addressLine2"
            labelText={formatMessage(messages.addressLine2)}
            component={InputTextWithValidationMessageAndToolip}
            tooltipText={formatMessage(messages.addressTooltip)}
          />
        </div>
        <div className="p-col-12">
          <Field
            name="company.postalCode"
            labelText={formatMessage(messages.postalCode)}
            component={InputTextWithValidationMessageAndToolip}
            tooltipText={formatMessage(messages.addressTooltip)}
          />
        </div>
        <div className="p-col-12">
          <Field
            name="company.city"
            labelText={formatMessage(messages.city)}
            component={InputTextWithValidationMessageAndToolip}
            tooltipText={formatMessage(messages.addressTooltip)}
          />
        </div>
        <div className="p-col-12">
          <Field
            name="company.state"
            labelText={formatMessage(messages.state)}
            component={InputTextWithValidationMessageAndToolip}
            tooltipText={formatMessage(messages.addressTooltip)}
          />
        </div>
        <div className="p-col-12">
          <label className="field-label" htmlFor="company.country">
            <FormattedMessage id="accept-client-account.org-step.country" defaultMessage="Country" />
          </label>
          <Field
            id="company.country"
            name="company.country"
            labelText={formatMessage(messages.country)}
            filter
            filterBy="label,value"
            options={countries}
            component={DropdownWithValidationAndTooltip}
            tooltipText={formatMessage(messages.addressTooltip)}
          />
        </div>
        <div className="p-col-12">
          <Field
            name="company.registrationNumber"
            labelText={formatMessage(messages.registrationNumber)}
            component={InputTextWithValidationMessageAndToolip}
            tooltipText={formatMessage(messages.regNoTooltip)}
          />
        </div>
        <TaxIdTypeInput
          countries={countries}
          availableTaxIds={availableTaxIds}
          taxIdOptions={taxIdOptions}
          setSelectedTaxId={setSelectedTaxId}
        />
        <TaxIdInput selectedTaxId={selectedTaxId} />
        <div className="p-col-12">
          <Field
            name="company.displayName"
            labelText={formatMessage(messages.displayName)}
            component={InputTextWithValidationMessageAndToolip}
            tooltipText={formatMessage(messages.displayNameTooltip)}
          />
        </div>
        <div className="p-col-12">
          <Field
            name="company.dpoEmail"
            type="email"
            labelText={formatMessage(messages.dpo)}
            component={InputTextWithValidationMessageAndToolip}
            tooltipText={formatMessage(messages.dpoEmailTooltip)}
          />
        </div>
        <br />
       <OrganisationSizeInput />
        <br />
        <div className="p-col-12">
          <Field
            name="company.creditNotificationThreshold"
            type="number"
            min="1"
            labelText={formatMessage(messages.creditNotificationThreshold)}
            component={InputTextWithValidationMessageAndToolip}
            tooltipText={formatMessage(messages.creditNotificationThresholdTooltip)}
          />
        </div>
        <div className="p-col-12">
          <Field
            id="client_terms_of_use_agreement"
            name="client_terms_of_use_agreement"
            component={ClientAgreementCheckbox}
          />
        </div>
        <EssentialAccountOrganizationDetailStepButtons userExists={userExists} token={token} />
      </Form>
    </Formik>
  );
};

export default EssentialAccountOrganizationDetailsStep;
